/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool,
    object, shape, string, arrayOf,
} from 'prop-types';
import { Grid } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import ReactMarkdown from 'react-markdown';
import { useUIDSeed } from 'react-uid';
import LinkOrATag from '../GraphqlCommonComponents/LinkOrATag/LinkOrATag';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { useDepartmentPageTracking } from '../../../helpers/tracking/page/DepartmentPageTrackingContext';

const styles = (theme) => ({
    mainContainer: {
        maxWidth: 1400,
        width: '100%',
        margin: '0px auto 60px auto',
    },
    innerContainerWidth: {
        borderTop: `2px dotted ${theme.palette.cms?.borderDashedColor || '#dddddd'}`,
        padding: '25px 0',
        '&:first-child': {
            marginTop: 20,
        },
        '&:last-child': {
            borderBottom: `2px dotted ${theme.palette.cms?.borderDashedColor || '#dddddd'}`,
            marginBottom: 20,
        },
    },
    headingMenuLink: {
        fontSize: '1.5em',
        fontWeight: '600',
        color: theme.palette.cms?.black || '#000000',
        textDecoration: 'none',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    headingMenuWithoutLink: {
        fontSize: '1.5em',
        fontWeight: '600',
        color: theme.palette.cms?.black || '#000000',
    },
    showHeadingOrLink: {
        minHeight: 275,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        paddingLeft: '3em',
    },
    mainItemList: {
        '& ul': {
            padding: '0px 0px 0px 4px',
            listStyle: 'none',
            margin: '2px 0px',
        },
    },
    itemListLink: {
        padding: '2px 0px',
        '& a': {
            color: theme.palette.cms?.black || '#000000',
            fontSize: 16,
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    },
    imageLinks: {
        '& img': {
            width: '100%',
        },
    },
    menuStyleLink: {
        padding: '5px 6px 5px 0px',
        borderBottom: `2px dotted ${theme.palette.cms?.borderDashedColor || '#dddddd'}`,
        '&:last-child': {
            borderBottom: 0,
            '& a': {
                textDecoration: 'underline',
            },
        },
        '& a': {
            color: theme.palette.cms?.black || '#000000',
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
    },
    innerBannerBottomContainer: {
        width: 740,
        display: 'flex',
        listStyle: 'none',
        justifyContent: 'space-evenly',
        margin: 'auto',
        '& a': {
            letterSpacing: '0.5px',
            color: theme.palette.cms?.contentColor || '#666',
        },
        '& li': {
            '&:first-child': {
                listStyle: 'none',
            },
        },
    },
    bannerBottomContainer: {
        width: '60%',
        margin: '0 auto',
        fontWeight: 'bold',
        color: theme.palette.cms?.contentColor || '#666',
    },
    onlyLinkList: {
        margin: '0',
    },
});

// showing left images block
const showImagesBlock = (classes, linkList, imagesData, trackDepartmentPage) => (
    linkList?.href ? (
        <LinkOrATag handleCallback={() => trackDepartmentPage(linkList?.title || 'ImageLinkList')} href={linkList?.href}>
            <ResponsiveImage className={classes.leftImage} path={imagesData?.url} params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} alt={imagesData?.description || 'Image Link List'} />
        </LinkOrATag>
    ) : <ResponsiveImage className={classes.leftImage} path={imagesData?.url} params={{ desktop: 'quality=100', tablet: 'quality=100', mobile: 'quality=100' }} alt={imagesData?.description || 'Image Link List'} />
);

// showing link or heading
const showLinkOrHeading = (classes, copyText, headingLinks, trackDepartmentPage) => (
    <div>
        {headingLinks?.href ? (
            <LinkOrATag handleCallback={() => trackDepartmentPage(headingLinks?.title || 'ImageLinkList')} className={classes.headingMenuLink} href={headingLinks?.href}>
                {headingLinks?.title}
            </LinkOrATag>
        ) : <div className={classes.headingMenuWithoutLink}>{headingLinks?.title}</div>}
        {copyText && <ReactMarkdown source={copyText} escapeHtml={false} />}
    </div>
);

// showing link list data
const linkListData = (classes, itemData, borderStyle, seed, trackDepartmentPage) => {
    const isBorderOnList = borderStyle?.border_for_link_list || false;
    let liStyle = {};
    liStyle = {
        fontSize: `${borderStyle?.link_list_font_size || 16}px`,
        fontWeight: borderStyle?.link_list_font_weight,
    };
    return (
        <li key={seed(itemData)} className={isBorderOnList ? classes.menuStyleLink : classes.itemListLink}><LinkOrATag handleCallback={() => trackDepartmentPage(itemData?.link?.title || 'ImageLinkList')} style={liStyle} href={itemData?.link?.href}>{itemData?.link?.title}</LinkOrATag></li>
    );
};

const ImageLinkListFeature = ({
    classes, data,
}) => {
    const seed = useUIDSeed();
    const trackDepartmentPage = useDepartmentPageTracking();
    const isShowLinkList = data?.image_link_list?.some((listData) => listData?.is_show_only_link_list);
    if (!data) return <></>;
    return (
        <Grid
            container
            className={`${isShowLinkList ? classes.mainLinkList : classes.mainContainer}`}
        >
            {data?.image_link_list?.map((imageList) => (
                <Grid
                    container
                    key={seed(imageList)}
                    className={`${imageList?.is_show_only_link_list ? classes.onlyLinkList : classes.innerContainerWidth}`}
                >
                    {imageList?.is_show_only_link_list ? (
                        <>
                            <Grid item xs={12}>
                                <div className={classes.bannerBottomContainer}>
                                    {imageList?.link_list.length > 0
                                    && (
                                        <ul
                                            className={classes.innerBannerBottomContainer}
                                            style={
                                                { listStyle: imageList?.link_list_style }
                                            }
                                        >
                                            {imageList?.link_list?.map((linkItem) => (
                                                linkListData(classes, linkItem, imageList, seed, trackDepartmentPage)
                                            ))}
                                        </ul>
                                    )                                    }
                                </div>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={7}>
                                <div className={classes.imageLinks}>
                                    {showImagesBlock(classes, imageList?.image_link, imageList?.image, trackDepartmentPage)}
                                </div>
                            </Grid>
                            <Grid item xs={5}>
                                <div className={classes.showHeadingOrLink}>
                                    {showLinkOrHeading(classes, imageList?.copy_for_link_list, imageList?.heading_link, trackDepartmentPage)}
                                    <div className={classes.LinkListStyle}>
                                        <div className={classes.mainItemList}>
                                            {imageList?.link_list.length > 0
                                            && (
                                                <ul>
                                                    {imageList?.link_list?.map((linkItem) => (
                                                        linkListData(classes, linkItem, imageList, seed, trackDepartmentPage)
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Grid>
                        </>
                    )}
                </Grid>
            ))}
        </Grid>
    );
};

ImageLinkListFeature.propTypes = {
    classes: object.isRequired,
    data: shape({
        image_link_list: arrayOf(shape({
            border_for_link_list: bool,
            copy_for_link_list: string,
            heading_link: shape({
                href: string,
                title: string,
            }),
            image: shape({
                url: string,
                title: string,
            }),
            image_link: shape({
                href: string,
                title: string,
            }),
            link_list: arrayOf(shape({
                link: shape({
                    href: string,
                    title: string,
                }),
            })),
        })),
    }),
};
ImageLinkListFeature.defaultProps = {
    data: {},
};
export default  withStyles(styles)(ImageLinkListFeature);
